import { Box, Group, rem, Stack, Text } from "@mantine/core";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  BoxSummary,
  StageBucket,
  stagePalette,
  stagePaletteShade,
  summariseBox,
} from "../utils/cptStageMetrics";

interface CPTStageBoxPlotProps {
  title: string;
  caption?: string;
  unit?: string;
  decimals?: number;
  stages: Array<{ stage: StageBucket; values: number[] }>;
  yMin?: number;
  yMax?: number;
  chartHeight?: number;
}

const PLOT_MARGIN = { top: 12, right: 12, bottom: 36, left: 72 };
const PLOT_WIDTH = 480;
const CAPTION_MIN_HEIGHT = rem(76);

function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return [min];
  }
  const step = (max - min) / count;
  return Array.from({ length: count + 1 }, (_, i) => min + i * step);
}

function pickScaleExponent(ticks: number[]): number | null {
  const maxAbs = Math.max(...ticks.map((t) => Math.abs(t)));
  if (!Number.isFinite(maxAbs) || maxAbs === 0) return null;
  if (maxAbs >= 0.1 && maxAbs < 1000) return null;
  return Math.floor(Math.log10(maxAbs));
}

function formatExponent(exp: number): string {
  const digits = "0123456789";
  const supers = "⁰¹²³⁴⁵⁶⁷⁸⁹";
  const sign = exp < 0 ? "⁻" : "";
  return (
    sign +
    Math.abs(exp)
      .toString()
      .split("")
      .map((d) => supers[digits.indexOf(d)] ?? d)
      .join("")
  );
}

const CPTStageBoxPlot: React.FC<CPTStageBoxPlotProps> = ({
  title,
  caption,
  unit,
  decimals = 0,
  stages,
  yMin,
  yMax,
  chartHeight = 220,
}) => {
  const { t } = useTranslation();
  const summaries = stages.map((s) => ({
    stage: s.stage,
    values: s.values,
    box: summariseBox(s.values),
  }));

  const allValues = summaries.flatMap((s) => s.values);
  const dataMin = allValues.length ? Math.min(...allValues) : 0;
  const dataMax = allValues.length ? Math.max(...allValues) : 1;
  let lo = typeof yMin === "number" ? yMin : dataMin;
  let hi = typeof yMax === "number" ? yMax : dataMax;
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  const pad = (hi - lo) * 0.08;
  if (typeof yMin !== "number") lo -= pad;
  if (typeof yMax !== "number") hi += pad;

  const plotWidth = PLOT_WIDTH - PLOT_MARGIN.left - PLOT_MARGIN.right;
  const plotInnerHeight = chartHeight;
  const totalHeight = plotInnerHeight + PLOT_MARGIN.top + PLOT_MARGIN.bottom;

  const yScale = (v: number) => {
    if (!Number.isFinite(v)) return PLOT_MARGIN.top;
    return PLOT_MARGIN.top + plotInnerHeight - ((v - lo) / (hi - lo)) * plotInnerHeight;
  };

  const bandWidth = plotWidth / Math.max(summaries.length, 1);
  const boxHalfWidth = Math.min(bandWidth * 0.28, 22);

  const ticks = niceTicks(lo, hi, 4);
  const scaleExp = pickScaleExponent(ticks);
  const scaleFactor = scaleExp === null ? 1 : Math.pow(10, scaleExp);

  const formatTick = (v: number) => {
    const scaled = v / scaleFactor;
    const effectiveDecimals = scaleExp === null ? decimals : Math.max(0, Math.min(decimals, 2));
    return scaled.toFixed(effectiveDecimals);
  };

  const scaledUnit = unit && scaleExp !== null ? `×10${formatExponent(scaleExp)} ${unit}` : unit;
  const axisLabel = scaledUnit ? `${title} (${scaledUnit})` : title;
  return (
    <Stack gap={6}>
      <Text size="sm" fw={700}>
        {title}
      </Text>
      {caption ? (
        <Text size="xs" c="dimmed" lh={1.4} mih={CAPTION_MIN_HEIGHT}>
          {caption}
        </Text>
      ) : null}
      <Box style={{ overflowX: "auto" }}>
        <svg
          role="img"
          aria-label={t("extraTests.continuousPerformance.result.boxPlot.ariaLabel", {
            title: title.toLowerCase(),
          })}
          viewBox={`0 0 ${PLOT_WIDTH} ${totalHeight}`}
          width="100%"
          style={{ display: "block", maxWidth: PLOT_WIDTH * 1.2 }}
        >
          {ticks.map((t, i) => {
            const y = yScale(t);
            return (
              <g key={`tick-${i}`}>
                <line
                  x1={PLOT_MARGIN.left}
                  x2={PLOT_MARGIN.left + plotWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--mantine-color-default-border)"
                  strokeDasharray={i === 0 || i === ticks.length - 1 ? undefined : "3 3"}
                  strokeWidth={1}
                />
                <text
                  x={PLOT_MARGIN.left - 6}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={10}
                  fill="var(--mantine-color-dimmed)"
                >
                  {formatTick(t)}
                </text>
              </g>
            );
          })}

          <g transform={`translate(14, ${PLOT_MARGIN.top + plotInnerHeight / 2}) rotate(-90)`}>
            <text
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill="var(--mantine-color-text)"
            >
              {axisLabel}
            </text>
          </g>

          {summaries.map((s, i) => {
            const cx = PLOT_MARGIN.left + (i + 0.5) * bandWidth;
            const box = s.box;
            const xLabel = cx;
            const yLabel = totalHeight - 18;
            const nLabel = totalHeight - 6;
            return (
              <g key={s.stage.key}>
                <text
                  x={xLabel}
                  y={yLabel}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--mantine-color-text)"
                >
                  {s.stage.shortLabel}
                </text>
                <text
                  x={xLabel}
                  y={nLabel}
                  textAnchor="middle"
                  fontSize={9}
                  fill="var(--mantine-color-dimmed)"
                >
                  {box
                    ? t("extraTests.continuousPerformance.result.boxPlot.countN", {
                        count: box.n,
                      })
                    : "—"}
                </text>
                {box ? renderBox(box, cx, boxHalfWidth, yScale, s.stage) : null}
              </g>
            );
          })}
        </svg>
      </Box>
      <Group gap="md" mt={4}>
        <LegendDot
          color="gray"
          label={t("extraTests.continuousPerformance.result.boxPlot.legend.baseline")}
        />
        <LegendDot
          color="indigo"
          label={t("extraTests.continuousPerformance.result.boxPlot.legend.visual")}
        />
        <LegendDot
          color="teal"
          label={t("extraTests.continuousPerformance.result.boxPlot.legend.auditory")}
        />
        <LegendDot
          color="violet"
          label={t("extraTests.continuousPerformance.result.boxPlot.legend.combined")}
        />
        <Text size="10px" c="dimmed">
          {t("extraTests.continuousPerformance.result.boxPlot.legend.footnote")}
        </Text>
      </Group>
    </Stack>
  );
};

function renderBox(
  box: BoxSummary,
  cx: number,
  half: number,
  yScale: (v: number) => number,
  stage: StageBucket,
) {
  const palette = stagePalette(stage);
  const shade = stagePaletteShade(palette);
  const color = `var(--mantine-color-${palette.hue}-${shade})`;
  const tint = `var(--mantine-color-${palette.hue}-${palette.intensity === "dark" ? 2 : 0})`;
  const yMin = yScale(box.min);
  const yMax = yScale(box.max);
  const yQ1 = yScale(box.q1);
  const yQ3 = yScale(box.q3);
  const yMed = yScale(box.median);
  return (
    <>
      <line x1={cx} x2={cx} y1={yMax} y2={yQ3} stroke={color} strokeWidth={1.2} />
      <line x1={cx} x2={cx} y1={yQ1} y2={yMin} stroke={color} strokeWidth={1.2} />
      <line
        x1={cx - half * 0.5}
        x2={cx + half * 0.5}
        y1={yMax}
        y2={yMax}
        stroke={color}
        strokeWidth={1.2}
      />
      <line
        x1={cx - half * 0.5}
        x2={cx + half * 0.5}
        y1={yMin}
        y2={yMin}
        stroke={color}
        strokeWidth={1.2}
      />
      <rect
        x={cx - half}
        y={yQ3}
        width={half * 2}
        height={Math.max(2, yQ1 - yQ3)}
        fill={tint}
        stroke={color}
        strokeWidth={1.2}
      />
      <line x1={cx - half} x2={cx + half} y1={yMed} y2={yMed} stroke={color} strokeWidth={2} />
    </>
  );
}

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Group gap={4} wrap="nowrap">
    <Box
      style={{
        width: 8,
        height: 8,
        borderRadius: 2,
        backgroundColor: `var(--mantine-color-${color}-6)`,
      }}
    />
    <Text size="10px" c="dimmed">
      {label}
    </Text>
  </Group>
);

export default CPTStageBoxPlot;
